import { arrayBufferToBase64, base64ToArrayBuffer } from "utils/convert-buffer"

const publicKeyPem = (process.env.SHELF_PUBLIC_KEY ?? "").replace(/\\n/g, "\n")

class ShelfEncrypt {
  static async encryptData(data: unknown) {
    try {
      // Convert data to string if it's an object
      const dataString = typeof data === "object" ? JSON.stringify(data) : String(data)
      const encodedData = new TextEncoder().encode(dataString)

      // Generate random IV
      const iv = window.crypto.getRandomValues(new Uint8Array(16))

      // Generate AES key
      const aesKey = await window.crypto.subtle.generateKey(
        {
          name: "AES-CBC",
          length: 256
        },
        true,
        ["encrypt", "decrypt"]
      )

      const [encryptedData, exportedAesKey, publicKey] = await Promise.all([
        // Encrypt the data with AES-CBC
        window.crypto.subtle.encrypt(
          {
            name: "AES-CBC",
            iv
          },
          aesKey,
          encodedData
        ),
        window.crypto.subtle.exportKey("raw", aesKey),
        this.importPublicKey(publicKeyPem)
      ])

      const encryptedKey = await window.crypto.subtle.encrypt(
        {
          name: "RSA-OAEP"
        },
        publicKey,
        exportedAesKey
      )

      return {
        iv: arrayBufferToBase64(iv),
        data: arrayBufferToBase64(encryptedData),
        encryptedKey: arrayBufferToBase64(encryptedKey)
      }
    } catch (error) {
      return {
        encryptedKey: "invalid_encryption"
      }
    }
  }

  private static async importPublicKey(publicKey: string) {
    const base64 = publicKey
      .replace(/-----BEGIN.*KEY-----/, "")
      .replace(/-----END.*KEY-----/, "")
      .replace(/\s+/g, "")

    const keyBuffer = base64ToArrayBuffer(base64)

    const recoveredPublicKey = await window.crypto.subtle.importKey(
      "spki",
      keyBuffer,
      {
        name: "RSA-OAEP",
        hash: { name: "SHA-256" }
      },
      true,
      ["encrypt"]
    )

    return recoveredPublicKey
  }
}

export default ShelfEncrypt
